"use client"

import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  incrementUsage,
  getUsageData,
  isNewsletterDismissed,
  dismissNewsletter,
  incrementVisitCount,
  type UsageData
} from '@/lib/usage-tracker'
import { useExitIntent } from './useExitIntent'

interface UseNewsletterPromptReturn {
  usageData: UsageData | null
  usageCount: number
  trackUsage: (toolName: string) => boolean
  showNewsletterModal: boolean
  closeNewsletterModal: () => void
  /**
   * Force-open the newsletter modal from a child component (e.g. a CTA inside
   * a results page). Bypasses the eligibility checks used by the passive
   * exit-intent / scroll triggers — callers are responsible for showing this
   * only on intentional user action.
   */
  openNewsletterModal: () => void
  /**
   * Stop the passive exit-intent/scroll modal for the REST OF THIS SESSION
   * without persisting a dismissal. Pages wire this to the inline capture
   * card's impression so a user who has already seen the in-frame ask is never
   * asked the same question twice on one screen. Unlike closeNewsletterModal
   * this never calls dismissNewsletter(), so the modal stays eligible on
   * future visits and on content pages.
   */
  suppressForSession: () => void
}

/**
 * Hook for tracking tool usage and showing newsletter signup prompt.
 *
 * TRIGGER LOGIC (post-2026-06 modal demotion):
 * - Authenticated users: never see the modal.
 * - Everyone else: exit intent OR 85% scroll only. The modal is now a passive
 *   exit-intent fallback — the inline ResultEmailCaptureCard owns the
 *   post-result ask, so the returning-visitor 35s post-calc timer was removed
 *   (it double-asked alongside the inline card).
 * - No 2-minute time fallback. The old fallback fired on engaged readers and
 *   was the worst-converting signal in the funnel.
 *
 * Usage:
 *   const { trackUsage, showNewsletterModal, closeNewsletterModal } = useNewsletterPrompt()
 *   trackUsage('fertilizer-calculator')
 */
interface UseNewsletterPromptOptions {
  /**
   * Scroll-depth trigger (0-1). Tools keep the conservative 0.85 default (the
   * inline result card owns the ask there); content pages (blog/guides/compare)
   * pass a lower threshold since a mid-article reader who leaves at 50% depth
   * would otherwise never be asked.
   */
  scrollThreshold?: number
  /**
   * Content-page scroll semantics: reaching threshold depth only ARMS the
   * trigger; it fires on a subsequent scroll-up (leave gesture) or at
   * read-complete depth. See useExitIntent.requireScrollUpAfterDepth.
   */
  requireScrollUpAfterDepth?: boolean
  /** Content-page mode: fire after this many ms without scrolling once armed. */
  idleAfterArmMs?: number
  /** Plain timed trigger (ms on page). Omit to disable. */
  timeThreshold?: number
  /** Quiet period before any signal can fire (ms). Default 5000. */
  initialDelay?: number
}

export function useNewsletterPrompt(options: UseNewsletterPromptOptions = {}): UseNewsletterPromptReturn {
  // Tool-page default lowered 0.85 → 0.65 (2026-07-13): the calculator sits in
  // the top half of the page and most tool users never reached 85% depth, so
  // the scroll trigger effectively never fired on mobile (mouseleave covered
  // desktop only).
  const { scrollThreshold = 0.65, requireScrollUpAfterDepth = false, idleAfterArmMs, timeThreshold, initialDelay = 5000 } = options
  const { user } = useAuth()
  const [showNewsletterModal, setShowNewsletterModal] = useState(false)
  const [usageData, setUsageData] = useState<UsageData | null>(null)
  const [hasShownModalThisSession, setHasShownModalThisSession] = useState(false)
  const hasShownRef = useRef(false)
  const [isEligible, setIsEligible] = useState(false)
  const isAuthenticated = !!user

  // Exit intent hook — mouseleave (desktop) or scroll signals (mobile). The
  // timed trigger is opt-in per surface; tool pages leave it off.
  const { hasTriggered: exitIntentTriggered } = useExitIntent({
    enabled: isEligible && !hasShownModalThisSession && !showNewsletterModal,
    scrollThreshold,
    requireScrollUpAfterDepth,
    idleAfterArmMs,
    timeThreshold,
    initialDelay
  })

  // On mount: increment visit count and check eligibility
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isAuthenticated) return

    // Increment visit count (only once per session)
    incrementVisitCount()

    // Check if user is eligible for newsletter modal
    const dismissed = isNewsletterDismissed()
    setIsEligible(!dismissed)
    setUsageData(getUsageData())
  }, [isAuthenticated])

  // Show modal when exit intent triggers (for eligible visitors)
  useEffect(() => {
    if (exitIntentTriggered && isEligible && !hasShownRef.current && !showNewsletterModal) {
      if (isNewsletterDismissed()) {
        setIsEligible(false)
        return
      }

      setShowNewsletterModal(true)
      setHasShownModalThisSession(true)
      hasShownRef.current = true
    }
  }, [exitIntentTriggered, isEligible, hasShownModalThisSession, showNewsletterModal])

  /**
   * Track usage of a tool (for analytics). The post-result email ask is now
   * owned by the inline ResultEmailCaptureCard; the modal stays as a passive
   * exit-intent fallback (see useExitIntent above), so there is no post-calc
   * timer here anymore.
   * @returns true if tracked, false if user is authenticated
   */
  const trackUsage = useCallback(
    (toolName: string): boolean => {
      // Don't track or show modal for authenticated users
      if (isAuthenticated) {
        return false
      }

      // Increment usage (always track for analytics)
      const updatedData = incrementUsage(toolName)
      setUsageData(updatedData)

      return true
    },
    [isAuthenticated]
  )

  const closeNewsletterModal = useCallback(() => {
    setShowNewsletterModal(false)
    // Mark as shown for this session so user isn't prompted again after dismissing
    setHasShownModalThisSession(true)
    hasShownRef.current = true
    // Persist dismissal so user won't see it in future sessions
    dismissNewsletter()
  }, [])

  const openNewsletterModal = useCallback(() => {
    if (isAuthenticated) return
    setShowNewsletterModal(true)
    setHasShownModalThisSession(true)
    hasShownRef.current = true
  }, [isAuthenticated])

  const suppressForSession = useCallback(() => {
    // In-memory only: disables the exit-intent trigger (isEligible gates it)
    // for this mount, leaves an already-open modal alone, and persists nothing.
    setIsEligible(false)
  }, [])

  return {
    usageData,
    usageCount: usageData?.count ?? 0,
    trackUsage,
    showNewsletterModal,
    closeNewsletterModal,
    openNewsletterModal,
    suppressForSession,
  }
}
